import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
} from "react";
import {
    AggregateFunctions,
    AggregateQuery,
    AggregateQueryInterval,
    AggregateQueryMode,
    ApiQuery,
    BasicFilter,
    CompositeFilter,
    Filter,
    FilterMetadata,
    FilterOperators,
    FilterTypes,
} from "~/modules/filters/types";
import { sortFilters } from "./sortFilters";
import * as Analytics from "../analytics";
import { useDebouncedState } from "~/utils/useDebouncedInput";

// // // //

const DEFAULT_COMPOSITE_FILTER: CompositeFilter = {
    operator: FilterOperators.And,
    filters: [],
};

const EMPTY_MULTI_SELECT_VALUE: string[] = [];

interface FilterFormContextType {
    filterMetadata: FilterMetadata[];
    currentFilters: BasicFilter[];
    aggregate: AggregateQuery;
    updateFilter: (updatedFilter: BasicFilter) => void;
    updateAggregate: (partial: Partial<AggregateQuery>) => void;
    resetFilters: () => void;
}

const FilterFormContext = createContext<FilterFormContextType | undefined>(
    undefined,
);

// // // //

const isFilterValueEmpty = (value: unknown): boolean => {
    return (
        value === "" ||
        value === null ||
        (Array.isArray(value) && value.length === 0)
    );
};

const getDefaultFilterValue = (
    existingFilter: BasicFilter | undefined,
    metadata: FilterMetadata | undefined,
): unknown => {
    if (existingFilter?.value !== undefined) {
        return existingFilter.value;
    }

    if (!metadata) {
        return "";
    }

    if (metadata.defaultValue !== undefined && metadata.defaultValue !== null) {
        return metadata.defaultValue;
    }

    if (metadata.type === FilterTypes.Boolean) {
        return false;
    }

    if (metadata.type === FilterTypes.StringSingleSelect) {
        return EMPTY_MULTI_SELECT_VALUE;
    }

    return "";
};

const getClearedFilterValue = (
    metadata: FilterMetadata | undefined,
): unknown => {
    if (!metadata) {
        return "";
    }

    if (metadata.type === FilterTypes.Boolean) {
        return null;
    }

    if (metadata.type === FilterTypes.StringSingleSelect) {
        return EMPTY_MULTI_SELECT_VALUE;
    }

    return "";
};

interface FilterFormFieldRenderProps {
    filterMetadata: FilterMetadata;
    value: unknown;
    onChange: (value: unknown) => void;
    clearValue: () => void;
}

interface FilterFormContextProviderProps {
    filterMetadata: FilterMetadata[];
    query: ApiQuery;
    onChange: (updatedApiQuery: ApiQuery) => void;
    children: React.ReactNode;
}

/**
 * FilterFormContextProvider
 * Shares filter metadata and query state with descendant FilterFormField components.
 */
export function FilterFormContextProvider(
    props: FilterFormContextProviderProps,
) {
    const { filterMetadata, query, onChange, children } = props;

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const queryRef = useRef(query);
    queryRef.current = query;

    const currentFilters = useMemo(() => {
        const filter = query.filter ?? DEFAULT_COMPOSITE_FILTER;
        return (filter.filters ?? []) as BasicFilter[];
    }, [query.filter]);

    const updateFilter = useCallback((updatedFilter: BasicFilter) => {
        const activeQuery = queryRef.current;
        const filter = activeQuery.filter ?? DEFAULT_COMPOSITE_FILTER;
        const filters = [...((filter.filters ?? []) as BasicFilter[])];
        const existingIndex = filters.findIndex(
            (entry) => entry.field === updatedFilter.field,
        );

        if (isFilterValueEmpty(updatedFilter.value)) {
            if (existingIndex >= 0) {
                filters.splice(existingIndex, 1);
            }
        } else if (existingIndex >= 0) {
            filters[existingIndex] = updatedFilter;
        } else {
            filters.push(updatedFilter);
        }

        const newCompositeFilter: Filter = {
            ...filter,
            filters: sortFilters([...filters]),
        } as Filter;

        onChangeRef.current({
            ...activeQuery,
            filter: newCompositeFilter,
        });
    }, []);

    const resetFilters = useCallback(() => {
        Analytics.trackInlineClearFilters();
        onChangeRef.current({
            ...queryRef.current,
            filter: undefined,
        });
    }, []);

    const aggregate = useMemo((): AggregateQuery => {
        const existing = query.aggregate;

        return {
            groupBy: existing?.groupBy ?? "",
            aggregateFunction:
                existing?.aggregateFunction ?? AggregateFunctions.Sum,
            interval: existing?.interval ?? AggregateQueryInterval.day,
            mode: existing?.mode ?? AggregateQueryMode.stock,
            groupLimit: existing?.groupLimit,
        };
    }, [query.aggregate]);

    const updateAggregate = useCallback((partial: Partial<AggregateQuery>) => {
        const activeQuery = queryRef.current;

        onChangeRef.current({
            ...activeQuery,
            aggregate: {
                ...activeQuery.aggregate,
                ...partial,
            } as AggregateQuery,
        });
    }, []);

    const value = useMemo(
        () => ({
            filterMetadata,
            currentFilters,
            aggregate,
            updateFilter,
            updateAggregate,
            resetFilters,
        }),
        [
            filterMetadata,
            currentFilters,
            aggregate,
            updateFilter,
            updateAggregate,
            resetFilters,
        ],
    );

    return (
        <FilterFormContext.Provider value={value}>
            {children}
        </FilterFormContext.Provider>
    );
}

// // // //

interface FilterFormFieldProps {
    column: string;
    debounce?: number;
    children: (props: FilterFormFieldRenderProps) => React.ReactNode;
}

interface FilterFormFieldContentProps {
    column: string;
    debounce?: number;
    children: (props: FilterFormFieldRenderProps) => React.ReactNode;
}

/**
 * FilterFormField
 * Connects a single filter input to the surrounding FilterFormContextProvider.
 */
export function FilterFormField(props: FilterFormFieldProps) {
    const isDebounced =
        typeof props.debounce === "number" && props.debounce > 0;

    if (isDebounced) {
        return (
            <FilterFormFieldDebounced
                column={props.column}
                debounce={props.debounce as number}
            >
                {props.children}
            </FilterFormFieldDebounced>
        );
    }

    return (
        <FilterFormFieldImmediate column={props.column}>
            {props.children}
        </FilterFormFieldImmediate>
    );
}

/**
 * FilterFormFieldImmediate
 * Filter field variant without debounced onChange behavior.
 */
function FilterFormFieldImmediate(props: FilterFormFieldContentProps) {
    const { column, children } = props;
    const fieldState = useFilterFormFieldState(column);

    if (!fieldState.metadata) {
        console.error(
            `FilterFormField: No metadata found for column "${column}"`,
        );
        return null;
    }

    return (
        <>
            {children({
                filterMetadata: fieldState.metadata,
                value: fieldState.currentValue,
                onChange: fieldState.handleUpdateFilter,
                clearValue: fieldState.handleClearValue,
            })}
        </>
    );
}

/**
 * FilterFormFieldDebounced
 * Filter field variant with debounced onChange behavior.
 */
function FilterFormFieldDebounced(
    props: FilterFormFieldContentProps & { debounce: number },
) {
    const { column, debounce, children } = props;
    const fieldState = useFilterFormFieldState(column);

    const [debouncedValue, setDebouncedValue] = useDebouncedState<unknown>({
        value: fieldState.currentValue,
        onChange: fieldState.handleUpdateFilter,
        debounceTimeout: debounce,
    });

    if (!fieldState.metadata) {
        console.error(
            `FilterFormField: No metadata found for column "${column}"`,
        );
        return null;
    }

    return (
        <>
            {children({
                filterMetadata: fieldState.metadata,
                value: debouncedValue,
                onChange: setDebouncedValue,
                clearValue: fieldState.handleClearValue,
            })}
        </>
    );
}

/**
 * useFilterFormFieldState
 * Shared field state for FilterFormField variants.
 */
function useFilterFormFieldState(column: string) {
    const context = useContext(FilterFormContext);

    if (!context) {
        throw new Error(
            "FilterFormField must be used within a FilterFormContextProvider",
        );
    }

    const { filterMetadata, currentFilters, updateFilter } = context;
    const metadata = filterMetadata.find((entry) => entry.field === column);
    const existingFilter = currentFilters.find(
        (entry) => entry.field === column,
    );
    const currentValue = getDefaultFilterValue(existingFilter, metadata);

    const handleUpdateFilter = useCallback(
        (newValue: unknown) => {
            if (!metadata) {
                return;
            }

            const operator =
                metadata.type === FilterTypes.Search
                    ? FilterOperators.Like
                    : metadata.allowedOperators.includes(FilterOperators.Equals)
                      ? FilterOperators.Equals
                      : metadata.defaultOperator;

            const newFilter: BasicFilter = {
                field: column,
                operator: existingFilter?.operator || operator,
                value: newValue as BasicFilter["value"],
            };

            updateFilter(newFilter);
        },
        [column, existingFilter?.operator, metadata, updateFilter],
    );

    const handleClearValue = useCallback(() => {
        handleUpdateFilter(getClearedFilterValue(metadata));
    }, [handleUpdateFilter, metadata]);

    return {
        metadata,
        currentValue,
        handleUpdateFilter,
        handleClearValue,
    };
}

// // // //

interface FilterFormResetProps {
    children: (props: { resetFilters: () => void }) => React.ReactNode;
}

interface AggregateQueryFieldProps<T> {
    children: (props: {
        value: T;
        onChange: (value: T) => void;
    }) => React.ReactNode;
}

/**
 * useAggregateQueryFieldState
 * Shared aggregate field state for FilterFormAggregate*Field components.
 */
function useAggregateQueryFieldState<K extends keyof AggregateQuery>(field: K) {
    const context = useContext(FilterFormContext);

    if (!context) {
        throw new Error(
            "Aggregate query fields must be used within a FilterFormContextProvider",
        );
    }

    const { aggregate, updateAggregate } = context;
    const value = aggregate[field];

    const onChange = useCallback(
        (newValue: AggregateQuery[K]) => {
            updateAggregate({ [field]: newValue } as Partial<AggregateQuery>);
        },
        [field, updateAggregate],
    );

    return {
        value,
        onChange,
    };
}

/**
 * FilterFormAggregateGroupByField
 * Connects a groupBy input to the surrounding FilterFormContextProvider.
 */
export function FilterFormAggregateGroupByField(
    props: AggregateQueryFieldProps<string>,
) {
    const { value, onChange } = useAggregateQueryFieldState("groupBy");

    return (
        <>
            {props.children({
                value: value ?? "",
                onChange,
            })}
        </>
    );
}

/**
 * FilterFormAggregateFunctionField
 * Connects an aggregateFunction input to the surrounding FilterFormContextProvider.
 */
export function FilterFormAggregateFunctionField(
    props: AggregateQueryFieldProps<AggregateFunctions>,
) {
    const { value, onChange } =
        useAggregateQueryFieldState("aggregateFunction");

    return (
        <>
            {props.children({
                value: value ?? AggregateFunctions.Sum,
                onChange,
            })}
        </>
    );
}

/**
 * FilterFormAggregateIntervalField
 * Connects an interval input to the surrounding FilterFormContextProvider.
 */
export function FilterFormAggregateIntervalField(
    props: AggregateQueryFieldProps<AggregateQueryInterval>,
) {
    const { value, onChange } = useAggregateQueryFieldState("interval");

    return (
        <>
            {props.children({
                value: value ?? AggregateQueryInterval.day,
                onChange,
            })}
        </>
    );
}

/**
 * FilterFormAggregateModeField
 * Connects a mode input to the surrounding FilterFormContextProvider.
 */
export function FilterFormAggregateModeField(
    props: AggregateQueryFieldProps<AggregateQueryMode>,
) {
    const { value, onChange } = useAggregateQueryFieldState("mode");

    return (
        <>
            {props.children({
                value: value ?? AggregateQueryMode.stock,
                onChange,
            })}
        </>
    );
}

// // // //

/**
 * FilterFormReset
 * Exposes resetFilters from the surrounding FilterFormContextProvider.
 */
export function FilterFormReset(props: FilterFormResetProps) {
    const { children } = props;
    const context = useContext(FilterFormContext);

    if (!context) {
        throw new Error(
            "FilterFormReset must be used within a FilterFormContextProvider",
        );
    }

    return <>{children({ resetFilters: context.resetFilters })}</>;
}
